import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardBody, Button, Spinner, Alert } from 'reactstrap';
import { toast } from 'react-hot-toast';
import CryptoJS from 'crypto-js';
import { getToken } from '../../../utility/AuthService';
import { useSelector } from 'react-redux';
import { selectLanguageUse } from '../../../redux/slices/language.Slice';

const ProgressAssessmentQuestions = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [assessmentData, setAssessmentData] = useState(null);
    const authToken = getToken();
    const languageUse = useSelector(selectLanguageUse);

    const loadAssessmentData = async () => {
        try {
            setIsLoading(true);
            setError(null);

            // Decrypt the submission ID
            const decryptedSubId = CryptoJS.AES.decrypt(id, 'mySecretKey').toString(CryptoJS.enc.Utf8);
            
            if (!decryptedSubId) {
                throw new Error('Invalid assessment ID');
            }

            // Import the service dynamically to avoid circular dependencies
            const { getProgressAssesmentListService } = await import('../../../services/submissionService');
            
            const result = await getProgressAssesmentListService({ authToken });
            
            if (result?.data?.statusCode === 200) {
                const assessment = result.data.data.list.find(item => item._id === decryptedSubId);
                
                if (assessment) {
                    setAssessmentData(assessment);
                } else {
                    throw new Error('Assessment not found');
                }
            } else {
                throw new Error(result?.data?.message || 'Failed to load assessment data');
            }
        } catch (error) {
            console.error('Error loading assessment data:', error);
            setError(
                languageUse === 'English'
                    ? `Failed to load assessment: ${error.message}`
                    : `Échec du chargement de l'évaluation: ${error.message}`
            );
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            loadAssessmentData();
        }
    }, [id]);

    const handleStartQuestions = () => {
        if (assessmentData) {
            // Navigate to the actual progress assessment questions
            navigate(`/progress/${id}`);
        }
    };

    const handleBackToHistory = () => {
        navigate('/user/history');
    };

    const handleViewDashboard = () => {
        if (assessmentData) {
            navigate(`/user/progress/dashboard/${id}`);
        }
    };

    if (isLoading) {
        return (
            <div className="container mt-4 text-center">
                <Spinner size="lg" color="primary" />
                <p className="mt-3 text-muted">
                    {languageUse === 'English' ? 'Loading assessment...' : 'Chargement de l\'évaluation...'}
                </p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mt-4">
                <div className="row justify-content-center">
                    <div className="col-md-8">
                        <Alert color="danger">
                            <h5>
                                <i className="bi bi-exclamation-triangle me-2"></i>
                                {languageUse === 'English' ? 'Error' : 'Erreur'}
                            </h5>
                            <p>{error}</p>
                            <Button color="outline-secondary" onClick={handleBackToHistory}>
                                <i className="bi bi-arrow-left me-2"></i>
                                {languageUse === 'English' ? 'Back to History' : 'Retour à l\'historique'}
                            </Button>
                        </Alert>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container mt-4">
            <div className="row justify-content-center">
                <div className="col-md-8">
                    <Card>
                        <CardHeader className="bg-info text-white">
                            <CardTitle tag="h4" className="mb-0">
                                <i className="bi bi-question-circle me-2"></i>
                                {languageUse === 'English' ? 'Progress Assessment Questions' : 'Questions d\'évaluation des progrès'}
                            </CardTitle>
                        </CardHeader>
                        <CardBody className="p-4">
                            {assessmentData && (
                                <div className="assessment-overview mb-4 p-3 bg-light rounded">
                                    <h6 className="mb-3">
                                        <i className="bi bi-info-circle me-2 text-info"></i>
                                        {languageUse === 'English' ? 'Assessment Overview' : 'Aperçu de l\'évaluation'}
                                    </h6>
                                    <div className="row">
                                        <div className="col-md-6">
                                            <p className="mb-2">
                                                <strong>
                                                    {languageUse === 'English' ? 'Status:' : 'Statut:'}
                                                </strong> 
                                                <span className={`badge ms-2 ${assessmentData.isDraft ? 'bg-warning' : 'bg-success'}`}>
                                                    {assessmentData.isDraft 
                                                        ? (languageUse === 'English' ? 'In Progress' : 'En cours')
                                                        : (languageUse === 'English' ? 'Completed' : 'Terminé')
                                                    }
                                                </span>
                                            </p>
                                            <p className="mb-2">
                                                <strong>
                                                    {languageUse === 'English' ? 'Created:' : 'Créé:'}
                                                </strong> 
                                                {new Date(assessmentData.createdAt).toLocaleDateString()}
                                            </p>
                                        </div>
                                        <div className="col-md-6">
                                            <p className="mb-2">
                                                <strong>
                                                    {languageUse === 'English' ? 'Last Updated:' : 'Dernière mise à jour:'}
                                                </strong> 
                                                {new Date(assessmentData.updatedAt).toLocaleDateString()}
                                            </p>
                                            <p className="mb-2">
                                                <strong>
                                                    {languageUse === 'English' ? 'ID:' : 'ID:'}
                                                </strong> 
                                                <code className="ms-2">{assessmentData._id.slice(-8)}</code>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="text-center mb-4">
                                <div className="mb-3">
                                    <i className="bi bi-clipboard-question" style={{ fontSize: '3rem', color: '#17a2b8' }}></i>
                                </div>
                                <h5 className="text-muted">
                                    {languageUse === 'English' 
                                        ? 'Ready to answer questions?' 
                                        : 'Prêt à répondre aux questions?'}
                                </h5>
                                <p className="text-muted">
                                    {languageUse === 'English'
                                        ? 'Continue your Progress Assessment by answering questions about your sustainability progress and improvements.'
                                        : 'Continuez votre évaluation des progrès en répondant aux questions sur vos progrès et améliorations en matière de durabilité.'}
                                </p>
                            </div>

                            <div className="d-grid gap-3">
                                <Button 
                                    color="info" 
                                    size="lg"
                                    onClick={handleStartQuestions}
                                    className="py-3"
                                >
                                    <i className="bi bi-play-circle me-2"></i>
                                    {languageUse === 'English' ? 'Continue Assessment' : 'Continuer l\'évaluation'}
                                </Button>
                                
                                <Button 
                                    color="outline-primary"
                                    onClick={handleViewDashboard}
                                    disabled={!assessmentData}
                                >
                                    <i className="bi bi-graph-up me-2"></i>
                                    {languageUse === 'English' ? 'View Dashboard' : 'Voir le tableau de bord'}
                                </Button>
                                
                                <Button 
                                    color="outline-secondary" 
                                    onClick={handleBackToHistory}
                                >
                                    <i className="bi bi-arrow-left me-2"></i>
                                    {languageUse === 'English' ? 'Back to History' : 'Retour à l\'historique'}
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default ProgressAssessmentQuestions;
